// @ts-check
// notifications.js — Schedule & task notifications for Web, Desktop, and iOS.
//
// Periodically checks the schedule cache for upcoming events and tasks,
// then fires native notifications via the platform-appropriate API.
// Notifications are sent:
//   - 15 minutes before a timed event/task starts
//   - At the start of the day (8 AM) for all-day events/tasks on that day

import { stripMarkdownText } from '../editor/preview-highlight.js';
import { getScheduleItems } from '../schedule/schedule.js';
import { toYYMMDD } from '../utils/dates.js';

const NOTIF_CHECK_INTERVAL = 60000; // check every 60 seconds
const NOTIF_ADVANCE_MINUTES = 15; // notify 15 min before timed events
const NOTIF_MORNING_HOUR = 8; // morning summary hour
const _notifiedKeys = new Set(); // prevent duplicate notifications

let _notifPermissionGranted = false;
/** @type {any} */
let _notifCheckTimer: any = null;
/** @type {any} */
let _midnightTimeout: any = null;
/** @type {any} */
let _midnightInterval: any = null;

// ── Permission request ──────────────────────────────────────────────────────

/** @returns {Promise<boolean>} */
async function requestNotificationPermission() {
  _notifPermissionGranted = !!(await window.Platform?.notifications?.request());
  return _notifPermissionGranted;
}

// ── Send notification ────────────────────────────────────────────────────────

/**
 * @param {string} title
 * @param {string} body
 * @param {string} [tag] - dedupe key; the same tag is sent at most once.
 * @returns {void}
 */
export function sendNotification(title: any, body: any, tag: any) {
  if (!_notifPermissionGranted) return;

  // Deduplicate: don't re-send the same notification
  if (_notifiedKeys.has(tag)) return;
  _notifiedKeys.add(tag);

  window.Platform?.notifications?.send({ title, body, tag });
}

// ── Check for upcoming notifications ─────────────────────────────────────────

/** @returns {Promise<void>} */
async function checkScheduleNotifications() {
  // Re-sync permission for Web/Desktop from the live API on every check.
  // This catches the case where the user grants permission in OS Settings while
  // the app is already running, without requiring an app restart.
  // Capacitor's LocalNotifications has no synchronous permission check, so that
  // path keeps using the cached flag (refreshed on each iOS app resume).
  if (!window.Platform?.isCapacitor && 'Notification' in window) {
    _notifPermissionGranted = Notification.permission === 'granted';
  }
  if (!_notifPermissionGranted) return;

  const now = new Date();
  const todayStr = toYYMMDD(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  // Typed via the function's inferred return so the .filter callbacks below get
  // contextual typing (a bare `let items;` would be implicit-any; `let items: any`
  // would strip the callback inference).
  let items: Awaited<ReturnType<typeof getScheduleItems>>;
  try {
    items = await getScheduleItems(todayStr);
  } catch (e: any) {
    console.warn('[notifications] Failed to fetch schedule items for today:', e);
    return;
  }

  // ── Morning summary for all-day events/tasks ──
  if (now.getHours() === NOTIF_MORNING_HOUR && now.getMinutes() < 2) {
    const allDay = items.filter((it) => it.isAllDay && !it.isCompleted);
    if (allDay.length > 0) {
      const events = allDay.filter((it) => !it.isTask);
      const tasks = allDay.filter((it) => it.isTask);
      const parts = [];
      if (events.length > 0) parts.push(`${events.length} event${events.length > 1 ? 's' : ''}`);
      if (tasks.length > 0) parts.push(`${tasks.length} task${tasks.length > 1 ? 's' : ''}`);
      sendNotification(
        "Today's Schedule",
        `You have ${parts.join(' and ')} today.`,
        `morning-${todayStr}`
      );
    }
  }

  // ── 15-minute advance notification for timed events/tasks ──
  const timed = items.filter((it) => !it.isAllDay && !it.isCompleted);
  for (const item of timed) {
    const startH = parseInt(item.startTime.slice(0, 2), 10);
    const startM = parseInt(item.startTime.slice(2), 10);
    const eventMinutes = startH * 60 + startM;
    const diff = eventMinutes - nowMinutes;

    const tagText = item.text || 'event';
    if (diff > 0 && diff <= NOTIF_ADVANCE_MINUTES) {
      const label = item.isTask ? 'Task' : 'Event';
      const cleanText = stripMarkdownText(item.text || '') || 'Upcoming scheduled item';
      sendNotification(
        `${label} in ${diff} minute${diff > 1 ? 's' : ''}`,
        cleanText,
        `advance-${todayStr}-${item.startTime}-${tagText}`
      );
    }

    // Also notify at the exact start time. Window is -1..1 to tolerate
    // the 60-second check interval drifting by up to one minute.
    if (diff >= -1 && diff <= 1) {
      const label = item.isTask ? 'Task' : 'Event';
      const cleanText = stripMarkdownText(item.text || '') || 'Scheduled item starting';
      sendNotification(
        `${label} starting now`,
        cleanText,
        `start-${todayStr}-${item.startTime}-${tagText}`
      );
    }
  }

  // ── Overdue task reminder (once per day at morning hour) ──
  // Check the past 7 days so tasks from earlier in the week are not missed.
  if (now.getHours() === NOTIF_MORNING_HOUR && now.getMinutes() < 2) {
    let overdueCount = 0;
    for (let daysBack = 1; daysBack <= 7; daysBack++) {
      const pastDate = new Date(now);
      pastDate.setDate(pastDate.getDate() - daysBack);
      const pastDateStr = toYYMMDD(pastDate);
      let pastItems: Awaited<ReturnType<typeof getScheduleItems>>;
      try {
        pastItems = await getScheduleItems(pastDateStr);
      } catch (e: any) {
        console.warn(`[notifications] Failed to fetch schedule items for ${pastDateStr}:`, e);
        continue;
      }
      overdueCount += pastItems.filter((it) => it.isTask && !it.isCompleted).length;
    }
    if (overdueCount > 0) {
      sendNotification(
        'Overdue Tasks',
        `You have ${overdueCount} overdue task${overdueCount > 1 ? 's' : ''}.`,
        `overdue-${todayStr}`
      );
    }
  }
}

// ── Start / stop notification checking ───────────────────────────────────────

/** @returns {Promise<void>} */
async function startNotifications() {
  const granted = await requestNotificationPermission();
  if (!granted) return;
  // Run an initial check
  checkScheduleNotifications();
  // Set up periodic checking
  if (_notifCheckTimer) clearInterval(_notifCheckTimer);
  _notifCheckTimer = setInterval(checkScheduleNotifications, NOTIF_CHECK_INTERVAL);
}

/** @returns {void} */
function stopNotifications() {
  if (_notifCheckTimer) {
    clearInterval(_notifCheckTimer);
    _notifCheckTimer = null;
  }
}

// Clear stale notification keys daily at midnight
/** @returns {void} */
function clearStaleNotificationKeys() {
  _notifiedKeys.clear();
}

/** @returns {void} */
function scheduleMidnightClear() {
  // Clear any previously scheduled timers before (re-)scheduling.
  if (_midnightTimeout) {
    clearTimeout(_midnightTimeout);
    _midnightTimeout = null;
  }
  if (_midnightInterval) {
    clearInterval(_midnightInterval);
    _midnightInterval = null;
  }

  const now = new Date();
  const msUntilMidnight =
    new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() - now.getTime();
  _midnightTimeout = setTimeout(() => {
    _midnightTimeout = null;
    clearStaleNotificationKeys();
    _midnightInterval = setInterval(clearStaleNotificationKeys, 86400000);
  }, msUntilMidnight);
}

// Auto-start notifications on load
if (document.readyState === 'complete') {
  startNotifications();
} else {
  window.addEventListener('load', startNotifications);
}

// On iOS, restart notifications when app resumes. Capacitor 7 exposes app
// lifecycle via Capacitor.Plugins.App.addListener('appStateChange', …); the
// legacy document 'resume'/'pause' events (Cordova-era) never fire under
// Capacitor, so the previous listeners were silent.
if (window.Platform?.isCapacitor) {
  const App = window.Capacitor?.Plugins?.App;
  if (App && typeof App.addListener === 'function') {
    App.addListener('appStateChange', ({ isActive }: any) => {
      if (isActive) {
        // Don't clear _notifiedKeys on resume: the dedup keys are date-stamped,
        // so they never cause stale dedup, and clearing today's keys would
        // re-fire (re-alert) notifications already sent for items still inside
        // their notify window on every foreground. The midnight timer owns reset.
        // scheduleMidnightClear is NOT called here; it runs once at module load.
        // startNotifications re-queries permissions and restarts the check interval.
        startNotifications();
      } else {
        stopNotifications();
      }
    });
  }
}

// Clear stale keys at midnight (anchored to actual midnight, not a rolling 24h interval)
scheduleMidnightClear(); // called once at module load; idempotent if called again

// Deferred shim: schedule/outbox.js still reads `sendNotification` as a
// runtime window global. Importing this module into outbox drags its
// app-state → note-editor → CM6 widget chain into outbox's isolated unit
// env, breaking outbox.test.js — so the import-repoint waits for that chain
// to lighten. startNotifications / stopNotifications /
// requestNotificationPermission had no remaining readers and were dropped.
if (typeof window !== 'undefined') {
  window.sendNotification = sendNotification;
}
